import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { DateField } from '../ui/DateField';
import { TimeField } from '../ui/TimeField';
import { combineDateTime, splitDueAt } from '../../lib/reminderDates';
import { toDateKey } from '../../lib/calendarDates';
import type { Reminder } from '../../lib/remindersApi';

export interface ReminderFormValues {
  title: string;
  description: string | null;
  dueAt: string;
  estimatedMinutes: number | null;
}

/**
 * Inline add/edit form shared by the AddReminderRow and the row editor.
 * Title + description + due date (themed DateField) + native time input (no
 * themed time picker exists yet; v1) + optional duration estimate in minutes.
 * Enter saves, Escape cancels.
 */
export function ReminderForm({
  reminder,
  busy,
  onSave,
  onCancel,
}: {
  reminder?: Reminder;
  busy?: boolean;
  onSave: (values: ReminderFormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const initial = reminder
    ? splitDueAt(reminder.dueAt)
    : { date: toDateKey(new Date()), time: '09:00' };
  const [title, setTitle] = useState(reminder?.title ?? '');
  const [description, setDescription] = useState(reminder?.description ?? '');
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [estimate, setEstimate] = useState(
    reminder?.estimatedMinutes != null ? String(reminder.estimatedMinutes) : '',
  );

  const estimateNum = estimate.trim() === '' ? null : Number(estimate);
  const estimateValid =
    estimateNum === null ||
    (Number.isInteger(estimateNum) && estimateNum >= 1 && estimateNum <= 1440);
  const valid = title.trim().length > 0 && date !== '' && time !== '' && estimateValid;

  function save() {
    if (!valid || busy) return;
    onSave({
      title: title.trim(),
      description: description.trim() || null,
      dueAt: combineDateTime(date, time),
      estimatedMinutes: estimateNum,
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3" onKeyDown={onKeyDown}>
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('reminders.titlePlaceholder')}
        className="ui-input"
      />
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('reminders.descriptionPlaceholder')}
        className="ui-input text-[13px]"
      />
      <div className="flex flex-wrap items-center gap-2">
        <DateField value={date} onChange={setDate} required className="w-40" />
        <TimeField value={time} onChange={setTime} required className="w-28" />
        <input
          type="number"
          min={1}
          max={1440}
          value={estimate}
          onChange={(e) => setEstimate(e.target.value)}
          placeholder={t('reminders.estimatePlaceholder')}
          aria-invalid={!estimateValid}
          className={`ui-input w-36 ${estimateValid ? '' : 'border-red-500'}`}
        />
      </div>
      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          disabled={!valid || busy}
          onClick={save}
          className="rounded-md bg-primary px-3 py-1.5 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {t('reminders.save')}
        </button>
      </div>
    </div>
  );
}
